import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  invoicesApi,
  type CalculateInvoicePayload,
  type CustomerInvoiceStatus,
  type InvoiceListParams,
  type RecordPaymentPayload,
  type SaveInvoicePayload,
} from '@/apis/invoices';
import { QUERY_KEYS } from '@/constants';

type ApiError = { response?: { data?: { message?: string } } };
const apiMessage = (error: ApiError, fallback: string) =>
  error.response?.data?.message || fallback;

export const useInvoices = (params: InvoiceListParams) =>
  useQuery({
    queryKey: [QUERY_KEYS.INVOICES, 'list', params],
    queryFn: () => invoicesApi.getInvoices(params),
    // Keep showing the previous rows while a search/filter refetches — the
    // table flashing to skeletons made the page height jump on every keystroke
    placeholderData: keepPreviousData,
  });

export const useInvoiceKpis = () =>
  useQuery({
    queryKey: [QUERY_KEYS.INVOICES, 'kpis'],
    queryFn: () => invoicesApi.getKpis(),
    staleTime: 5 * 60 * 1000,
  });

export const useInvoiceDetail = (id: string | null) =>
  useQuery({
    queryKey: [QUERY_KEYS.INVOICES, 'detail', id],
    queryFn: () => invoicesApi.getInvoiceDetail(id!),
    enabled: !!id,
  });

const invalidateInvoices = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: [QUERY_KEYS.INVOICES] });

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.deleteInvoice(id),
    onSuccess: () => {
      invalidateInvoices(queryClient);
      toast.success('Invoice deleted');
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to delete invoice')),
  });
};

// No bulk endpoint on the BE — PATCH each selected invoice individually and
// report partial failures instead of failing the whole batch.
export const useBulkUpdateInvoiceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      status,
    }: {
      ids: string[];
      status: CustomerInvoiceStatus;
    }) => {
      const results = await Promise.allSettled(
        ids.map((id) => invoicesApi.updateInvoice(id, { status })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { failed, total: ids.length };
    },
    onSuccess: ({ failed, total }) => {
      invalidateInvoices(queryClient);
      if (failed) {
        toast.warning(`Updated ${total - failed} of ${total} invoices — ${failed} failed`);
      } else {
        toast.success(`Updated ${total} invoice${total === 1 ? '' : 's'}`);
      }
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to update invoices')),
  });
};

export const useCalculateInvoice = () =>
  useMutation({
    mutationFn: (payload: CalculateInvoicePayload) =>
      invoicesApi.calculate(payload),
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Could not calculate invoice totals')),
  });

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveInvoicePayload) => invoicesApi.createInvoice(data),
    onSuccess: (invoice) => {
      invalidateInvoices(qc);
      const label =
        invoice.documentType === 'credit_memo' ? 'Credit memo' : 'Invoice';
      if (invoice.emailError) {
        toast.warning(`${label} created — email not sent: ${invoice.emailError}`);
      } else {
        toast.success(`${label} created`);
      }
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to create invoice')),
  });
};

export const useUpdateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<SaveInvoicePayload>;
    }) => invoicesApi.updateInvoice(id, payload),
    onSuccess: (invoice) => {
      invalidateInvoices(qc);
      if (invoice.emailError) {
        toast.warning(
          `${invoice.documentType === 'credit_memo' ? 'Credit memo' : 'Invoice'} updated — email not sent: ${invoice.emailError}`,
        );
      } else {
        toast.success(
          `${invoice.documentType === 'credit_memo' ? 'Credit memo' : 'Invoice'} updated`,
        );
      }
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to update invoice')),
  });
};

export const useSendInvoiceEmail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.sendInvoiceEmail(id),
    onSuccess: () => {
      invalidateInvoices(qc);
      toast.success('Invoice email sent');
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to send invoice email')),
  });
};

export const useAddInvoicePayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: RecordPaymentPayload;
    }) => invoicesApi.addPayment(id, payload),
    onSuccess: () => {
      invalidateInvoices(qc);
      toast.success('Payment recorded');
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to record payment')),
  });
};

export const useRemoveInvoicePayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      invoicesApi.removePayment(id, paymentId),
    onSuccess: () => {
      invalidateInvoices(qc);
      toast.success('Payment removed');
    },
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Failed to remove payment')),
  });
};

export const useParseInvoiceItemsUpload = () =>
  useMutation({
    mutationFn: (file: File) => invoicesApi.parseItemsUpload(file),
    onError: (error: ApiError) =>
      toast.error(apiMessage(error, 'Could not read that file')),
  });
